import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp, TrendingDown, Minus, Plus, Calendar, Play, Edit3, Trash2,
  Download, FileText, Table, PieChart, BarChart3, Filter, ChevronDown,
  Printer, LineChart as LineChartIcon, Target, Loader2,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar,
} from "recharts";
import {
  getKpis, getPipeline, getSourceAttribution, getHiringGoals,
  getSavedReports, queryMetrics, previewReport, METRIC_KEY_BY_LABEL,
} from "../../../api/reports";

// Every widget here is backed by a real common-service endpoint (see
// src/api/reports.js). Some of them legitimately return nothing on a young
// tenant — those render an EmptyState rather than a plausible-looking number.
// This screen previously shipped entirely hardcoded sample data.

// Palette reused for series/bars so colour assignment stays stable per index.
const SERIES_COLORS = [
  "#7B5AC4", "#2E7DAF", "#1F8A7D", "#B865A8", "#B8942F", "#D4572D", "#16A34A",
];
const colorAt = (i) => SERIES_COLORS[i % SERIES_COLORS.length];

/**
 * One widget = one request. Returns { data, loading, error } and never throws,
 * so a single failing endpoint degrades that card instead of the whole page.
 */
function useWidget(fetcher, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve()
      .then(fetcher)
      .then((r) => {
        if (!cancelled) setState({ data: r?.data ?? null, loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled)
          setState({ data: null, loading: false, error: e?.message || "Failed to load." });
      });
    return () => {
      cancelled = true;
    };
    /* eslint-disable-next-line */
  }, deps);
  return state;
}

/**
 * queryMetrics rows come straight from amplehire_analytics_metric_t:
 *   { metricKey, value, count, department, source, stage, timePeriod, jobId }
 * Pivot them into recharts rows keyed by timePeriod, with one series per
 * department when present, else per metricKey.
 */
function pivotMetricRows(rows) {
  const byPeriod = new Map();
  const seriesKeys = new Set();
  for (const r of rows) {
    const x = r.timePeriod || "";
    const series = r.department || r.source || r.metricKey || "value";
    const value = Number(r.value ?? r.count ?? 0);
    seriesKeys.add(series);
    if (!byPeriod.has(x)) byPeriod.set(x, { month: x });
    byPeriod.get(x)[series] = value;
  }
  return {
    tthTrend: [...byPeriod.values()].sort((a, b) => String(a.month).localeCompare(String(b.month))),
    deptLines: [...seriesKeys].map((key, i) => ({ key, color: colorAt(i) })),
  };
}

/** Shared card chrome so every widget shows loading / error / empty the same way. */
function WidgetShell({ title, delay = 0, loading, error, isEmpty, emptyIcon, emptyTitle, emptyHint, children }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay }} className="card-glass" style={{ borderRadius: 12, padding: 20 }}>
      <h3 style={{ fontSize: 11, fontWeight: 600, color: "var(--ah-muted)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 16px" }}>{title}</h3>
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0", gap: 8, color: "var(--ah-muted)", fontSize: 12 }}>
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : error ? (
        <EmptyState icon={Filter} title="Couldn't load this widget" subtitle={error} color="#D4572D" />
      ) : isEmpty ? (
        <EmptyState icon={emptyIcon} title={emptyTitle} subtitle={emptyHint} color="#64748B" />
      ) : (
        children
      )}
    </motion.div>
  );
}

// ── Inline EmptyState (matches the SOURCE ui/EmptyState props used here) ──
const EmptyState = ({ icon: Icon, title, subtitle, color = "#7B5AC4" }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 16px", textAlign: "center", gap: 8 }}>
    <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {Icon && <Icon size={22} style={{ color }} />}
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ah-text)" }}>{title}</div>
    {subtitle && <div style={{ fontSize: 12, color: "var(--ah-muted)", maxWidth: 320 }}>{subtitle}</div>}
  </div>
);

// ── Animated Counter ──
function AnimatedCounter({ value, duration = 1500 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(0);
  useEffect(() => {
    const start = ref.current;
    const diff = value - start;
    const t0 = performance.now();
    let raf;
    function tick(t) {
      const p = Math.min((t - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      const v = start + diff * e;
      setDisplay(v);
      ref.current = v;
      if (p < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  if (value >= 100) return <>{Math.round(display).toLocaleString("en-IN")}</>;
  return <>{display.toFixed(1)}</>;
}

// ── Mini Sparkline SVG ──
function MiniSparkline({ data, color }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const h = 24;
  const w = 80;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(" ");
  return <svg width={w} height={h} style={{ opacity: 0.8 }}><polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

// ── KPI Card ──
function KPICard({ kpi, i }) {
  const Icon = kpi.trend === "up" ? TrendingUp : kpi.trend === "down" ? TrendingDown : Minus;
  const tc = kpi.trend === "up" ? "var(--accent-green)" : kpi.trend === "down" ? "var(--accent-red)" : "var(--ah-muted)";
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
      className="card-glass-elevated card-3d" style={{ borderRadius: 12, padding: 16, cursor: "default" }} whileHover={{ y: -2, boxShadow: "0 8px 30px rgba(0,0,0,0.08)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ah-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{kpi.label}</span>
        <div style={{ width: 28, height: 28, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: `${kpi.color}15` }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: kpi.color }} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
        <div>
          <div className="font-kpi" style={{ fontSize: 22, fontWeight: 700, color: "var(--ah-text)" }}>
            {kpi.prefix}<AnimatedCounter value={kpi.value} />{kpi.unit && <span style={{ fontSize: 13, color: "var(--ah-muted)", marginLeft: 3 }}>{kpi.unit}</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <Icon size={11} style={{ color: tc }} />
            <span style={{ fontSize: 11, fontWeight: 500, color: tc }}>{kpi.trendPct > 0 ? "+" : ""}{kpi.trendPct}%</span>
            <span style={{ fontSize: 10, color: "var(--ah-muted)", marginLeft: 2 }}>vs last qtr</span>
          </div>
        </div>
        <MiniSparkline data={kpi.sparkline} color={kpi.color} />
      </div>
    </motion.div>
  );
}

// ── SVG Application Funnel ──
function ApplicationFunnel({ period }) {
  const [hovered, setHovered] = useState(null);
  // dashboard/pipeline returns { stages: [{ stage, value, drop, isBiggestDrop }] }.
  const { data, loading, error } = useWidget(() => getPipeline(period), [period]);
  const funnelData = (data?.stages || []).map((st, i) => ({
    stage: st.stage,
    count: Number(st.value) || 0,
    color: colorAt(i),
  }));
  const hasAny = funnelData.some((d) => d.count > 0);
  const w = 500;
  const rowH = 56;
  const gap = 8;
  const h = funnelData.length * (rowH + gap);
  const maxW = w - 80;
  const r = 6;

  return (
    <WidgetShell
      title="Application Funnel"
      delay={0.5}
      loading={loading}
      error={error}
      isEmpty={!hasAny}
      emptyIcon={Filter}
      emptyTitle="No pipeline activity yet"
      emptyHint="Stages fill in once candidates are invited and start interviewing."
    >
      <svg width="100%" viewBox={`0 0 ${w} ${h + 20}`} style={{ overflow: "visible" }}>
        <defs>
          <filter id="funnel-glow">
            <feDropShadow dx="0" dy="2" stdDeviation="6" floodOpacity="0.25" />
          </filter>
        </defs>
        {funnelData.map((d, i) => {
          const pct = Math.max(d.count / funnelData[0].count, 0.25);
          const nextPct = i < funnelData.length - 1 ? Math.max(funnelData[i + 1].count / funnelData[0].count, 0.25) : pct * 0.8;
          const topW = Math.max(maxW * pct, 120);
          const botW = Math.max(maxW * nextPct, 120);
          const y = i * (rowH + gap);
          const cx = w / 2;
          const topL = cx - topW / 2;
          const topR = cx + topW / 2;
          const botL = cx - botW / 2;
          const botR = cx + botW / 2;
          const conv = i < funnelData.length - 1 ? ((funnelData[i + 1].count / d.count) * 100).toFixed(1) : null;
          const totalPct = ((d.count / funnelData[0].count) * 100).toFixed(1);
          const isH = hovered === i;
          const isNarrow = topW < 160;

          const path = `M${topL + r},${y} L${topR - r},${y} Q${topR},${y} ${topR},${y + r} L${botR},${y + rowH - r} Q${botR},${y + rowH} ${botR - r},${y + rowH} L${botL + r},${y + rowH} Q${botL},${y + rowH} ${botL},${y + rowH - r} L${topL},${y + r} Q${topL},${y} ${topL + r},${y} Z`;

          return (
            <g key={d.stage} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} style={{ cursor: "default" }}>
              <motion.path
                d={path}
                fill={d.color} opacity={isH ? 1 : 0.85}
                filter={isH ? "url(#funnel-glow)" : undefined}
                initial={{ opacity: 0, scale: 0.3 }} animate={{ opacity: isH ? 1 : 0.85, scale: 1 }}
                transition={{ delay: 0.6 + i * 0.12, duration: 0.6, ease: "backOut" }}
                style={{ transformOrigin: `${cx}px ${y + rowH / 2}px`, transition: "opacity 0.2s, filter 0.2s" }}
              />
              {isNarrow ? (
                <text x={topL - 8} y={y + rowH / 2 + 1} textAnchor="end" fill={d.color} fontSize="13" fontWeight="600">{d.stage}</text>
              ) : (
                <text x={cx} y={y + rowH / 2 - 7} textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700">{d.stage}</text>
              )}
              <text x={cx} y={y + rowH / 2 + (isNarrow ? 1 : 11)} textAnchor="middle" fill={isNarrow ? "#fff" : "rgba(255,255,255,0.9)"} fontSize="11" fontWeight="600" className="font-kpi">{d.count.toLocaleString()}</text>
              {conv && (
                <>
                  <text x={w - 8} y={y + rowH / 2 - 4} textAnchor="end" fill="var(--ah-muted)" fontSize="10" fontWeight="500">{conv}% conv</text>
                  <text x={w - 8} y={y + rowH / 2 + 10} textAnchor="end" fill="var(--ah-muted)" fontSize="9">{totalPct}% of total</text>
                </>
              )}
              {i < funnelData.length - 1 && (
                <g>
                  <line x1={cx} y1={y + rowH + 1} x2={cx} y2={y + rowH + gap - 1} stroke="var(--ah-muted)" strokeWidth="1" strokeDasharray="2 2" opacity="0.4" />
                  <polygon points={`${cx - 3},${y + rowH + gap - 3} ${cx + 3},${y + rowH + gap - 3} ${cx},${y + rowH + gap}`} fill="var(--ah-muted)" opacity="0.4" />
                </g>
              )}
              {isH && (
                <g>
                  <rect x={cx - 80} y={y - 36} width={160} height={30} rx={6} fill="var(--ah-text)" opacity="0.92" />
                  <text x={cx} y={y - 17} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="500">
                    {d.stage}: {d.count.toLocaleString()} ({totalPct}%){conv ? ` | ${conv}% conv` : ""}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </WidgetShell>
  );
}

// ── Source Attribution Horizontal Bar ──
function SourceAttribution({ period }) {
  // /analytics/source-attribution → { channels:[{channel,count,breakdown}], topSources:[...] }
  const { data, loading, error } = useWidget(() => getSourceAttribution(period), [period]);
  const sourceData = (data?.topSources || []).map((s, i) => ({
    name: s.source,
    count: Number(s.count) || 0,
    color: colorAt(i),
  }));
  const maxVal = sourceData.length ? Math.max(...sourceData.map((s) => s.count)) : 0;
  return (
    <WidgetShell
      title="Source Attribution"
      delay={0.6}
      loading={loading}
      error={error}
      isEmpty={sourceData.length === 0}
      emptyIcon={PieChart}
      emptyTitle="No application sources yet"
      emptyHint="Only career-page applications are attributed here — resume uploads and pool imports carry no source."
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {sourceData.map((s, i) => (
          <motion.div key={s.name} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 + i * 0.06 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: "var(--ah-text)", fontWeight: 500 }}>{s.name}</span>
              <span className="font-kpi" style={{ fontSize: 12, fontWeight: 600, color: s.color }}>{s.count}</span>
            </div>
            <div style={{ height: 8, backgroundColor: "var(--ah-surface)", borderRadius: 4, overflow: "hidden" }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${(s.count / maxVal) * 100}%` }}
                transition={{ delay: 0.8 + i * 0.06, duration: 0.6, ease: "easeOut" }}
                style={{ height: "100%", backgroundColor: s.color, borderRadius: 4 }} />
            </div>
          </motion.div>
        ))}
      </div>
    </WidgetShell>
  );
}

// ── Time to Hire Trend (recharts) ──
function TimeToHireTrend() {
  // /analytics/query → { data: [{ period, <metric_key>: value }, ...] }. The engine
  // keys rows by metric name, so the series are whatever it actually returned
  // rather than a fixed department list (departments aren't modelled here).
  const { data, loading, error } = useWidget(
    () => queryMetrics(["time_to_hire"], { granularity: "MONTH" }),
    [],
  );
  const { tthTrend, deptLines } = pivotMetricRows(data?.data || []);
  return (
    <WidgetShell
      title="Time to Hire Trend (Days)"
      delay={0.7}
      loading={loading}
      error={error}
      isEmpty={tthTrend.length === 0 || deptLines.length === 0}
      emptyIcon={LineChartIcon}
      emptyTitle="Not enough hires to trend"
      emptyHint="Time to hire needs completed hires across at least one period."
    >
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={tthTrend} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#94A3B8", fontSize: 9 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, fontSize: 11 }} />
            {deptLines.map((d) => (
              <Line key={d.key} type="monotone" dataKey={d.key} stroke={d.color} strokeWidth={d.key === "Overall" ? 2.5 : 1.5}
                dot={false} strokeDasharray={d.key === "Overall" ? undefined : "4 3"} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
        {deptLines.map((d) => (
          <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 10, height: 3, backgroundColor: d.color, borderRadius: 2 }} />
            <span style={{ fontSize: 10, color: "var(--ah-muted)" }}>{d.key}</span>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

// ── Hiring Goals Progress ──
function HiringGoals({ period }) {
  // dashboard/hiring-goals → { quarterLabel, goals:[{ team, hired, target, ... }] }
  const { data, loading, error } = useWidget(() => getHiringGoals(period), [period]);
  const goalData = (data?.goals || []).map((g, i) => ({
    dept: g.team || g.department || "Unassigned",
    hired: Number(g.hired ?? g.achieved ?? 0),
    goal: Number(g.target ?? g.goal ?? 0),
    color: colorAt(i),
  }));
  return (
    <WidgetShell
      title="Hiring Goals Progress"
      delay={0.8}
      loading={loading}
      error={error}
      isEmpty={goalData.length === 0}
      emptyIcon={Target}
      emptyTitle="No hiring goals set"
      emptyHint={`Create a goal for ${data?.quarterLabel || "this quarter"} from the dashboard to track progress here.`}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {goalData.map((g, i) => {
          const pct = Math.round((g.hired / g.goal) * 100);
          const barColor = pct >= 75 ? "var(--accent-green)" : pct >= 50 ? "var(--accent-amber)" : "var(--accent-red)";
          return (
            <motion.div key={g.dept} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.9 + i * 0.05 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Target size={12} style={{ color: g.color }} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--ah-text)" }}>{g.dept}</span>
                </div>
                <span className="font-kpi" style={{ fontSize: 12, fontWeight: 600, color: barColor }}>{g.hired}/{g.goal} ({pct}%)</span>
              </div>
              <div style={{ height: 6, backgroundColor: "var(--ah-surface)", borderRadius: 3, overflow: "hidden" }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(pct, 100)}%` }}
                  transition={{ delay: 1 + i * 0.05, duration: 0.6 }}
                  style={{ height: "100%", backgroundColor: barColor, borderRadius: 3 }} />
              </div>
            </motion.div>
          );
        })}
      </div>
    </WidgetShell>
  );
}

// ── Static KPIs ──
// KPI tiles come from dashboard/kpis. That endpoint reports openJobs /
// activeCandidates / interviewsWeek / avgScore — it has no notion of cost per
// hire or source effectiveness, so those two tiles are gone rather than faked.
const KPI_TILE_STYLE = {
  openJobs: { color: "#1F8A7D", unit: "" },
  activeCandidates: { color: "#2E7DAF", unit: "" },
  interviewsWeek: { color: "#B865A8", unit: "" },
  avgScore: { color: "#B8942F", unit: "" },
};

/** dashboard/kpis tile -> the shape KPICard expects. */
function toKpiCard(tile) {
  const style = KPI_TILE_STYLE[tile.key] || { color: "#7B5AC4", unit: "" };
  const delta = Number(tile.delta) || 0;
  return {
    label: tile.label,
    value: Number(tile.value) || 0,
    unit: tile.deltaUnit === "%" ? style.unit : style.unit,
    color: style.color,
    trend: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
    trendPct: delta,
    sparkline: Array.isArray(tile.spark) ? tile.spark.map(Number) : [],
  };
}

const moduleBadgeColors = {
  Pipeline: "#16A34A", Analytics: "#2E7DAF", Sourcing: "#1F8A7D", Interviews: "#B865A8",
  Offers: "#16A34A", Team: "#64748B", Jobs: "#2E7DAF", Assessments: "#B865A8",
  Goals: "#B8942F", Finance: "#B8942F", Compliance: "#64748B",
};

const metricOptions = ["Time to Hire", "Applications", "Interviews", "Offers", "Source Attribution", "Diversity"];
const dimensionOptions = ["Department", "Job", "Source", "Stage", "Recruiter", "Time Period"];
const chartTypes = [
  { type: "line", icon: LineChartIcon, label: "Line" },
  { type: "bar", icon: BarChart3, label: "Bar" },
  { type: "donut", icon: PieChart, label: "Donut" },
  { type: "table", icon: Table, label: "Table" },
  { type: "funnel", icon: Filter, label: "Funnel" },
];


// ── Main Component ──
export default function Reports() {
  const [builderOpen, setBuilderOpen] = useState(false);
  const [metric, setMetric] = useState(metricOptions[0]);
  const [dimension, setDimension] = useState(dimensionOptions[0]);
  const [chartType, setChartType] = useState("bar");
  const [generated, setGenerated] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const period = "30d";

  const kpiState = useWidget(() => getKpis(period), [period]);
  const kpis = (kpiState.data?.tiles || []).map(toKpiCard);

  // Saved reports + tenant-visible templates.
  const savedState = useWidget(() => getSavedReports(), []);
  const reportsLoading = savedState.loading;
  const savedReports = (savedState.data || []).map((r) => ({
    id: String(r.id),
    name: r.title || r.name || "Untitled report",
    description: r.description || [r.metrics, r.dimensions].filter(Boolean).join(" · "),
    module: r.module || (r.isTemplate ? "Template" : "Analytics"),
    icon: r.isTemplate ? "🧩" : "📊",
    records: r.records ?? null,
    lastRun: r.lastRunAt || r.updatedAt || r.createdAt || null,
  }));

  // Report-builder preview. Runs on demand from the Generate button.
  const [previewRows, setPreviewRows] = useState([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const runPreview = async () => {
    const key = METRIC_KEY_BY_LABEL[metric];
    setPreviewBusy(true);
    setPreviewError(null);
    setGenerated(true);
    try {
      const r = await previewReport(key ? [key] : []);
      const rows = r.data?.data || [];
      // Engine rows are { period, <metric_key>: number }. Flatten to name/value
      // so the bar/line chart can render whatever metric came back.
      setPreviewRows(
        rows.map((row) => {
          const numeric = Object.entries(row).find(
            ([k, v]) => k !== "period" && typeof v === "number",
          );
          return {
            name: String(row.period ?? row.month ?? ""),
            value: numeric ? Number(numeric[1]) : 0,
          };
        }),
      );
    } catch (e) {
      setPreviewRows([]);
      setPreviewError(e?.message || "Preview failed.");
    } finally {
      setPreviewBusy(false);
    }
  };
  const generatedBarData = previewRows;

  // Diversity + recruiter activity both come out of the analytics metric store
  // (amplehire_analytics_metric_t) via /analytics/query. Nothing populates that
  // table on this deployment yet, so both legitimately render empty.
  const diversityState = useWidget(() => queryMetrics(["diversity_pipeline"]), []);
  const diversityRows = (diversityState.data?.data || []).map((r, i) => {
    const v = Number(r.value ?? r.count ?? 0);
    return {
      label: r.stage || r.department || r.source || r.metricKey || "Diversity",
      value: `${v}%`,
      color: colorAt(i),
      bar: Math.max(0, Math.min(100, v)),
    };
  });

  const recruiterState = useWidget(() => queryMetrics(["recruiter_activity"]), []);
  const recruiterRows = (recruiterState.data?.data || []).map((r, i) => ({
    name: r.department || r.source || `Recruiter ${i + 1}`,
    hires: Number(r.value ?? 0),
    screenings: Number(r.count ?? 0),
    avgTTH: "—",
    color: colorAt(i),
  }));

  const selStyle = (active) => ({
    padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: "pointer", border: "none",
    backgroundColor: active ? "var(--ah-primary)" : "var(--ah-surface)", color: active ? "#fff" : "var(--ah-text)",
    transition: "all 0.2s",
  });

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="page-container" style={{ position: "relative", minWidth: 0, overflow: "hidden" }}>
      {/* Floating orbs */}
      <div className="orb orb-violet" style={{ top: -40, right: -60 }} />
      <div className="orb orb-teal" style={{ bottom: 80, left: -50 }} />
      <div className="orb orb-amber" style={{ top: "40%", right: -30 }} />

      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: "#B8942F1F", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <BarChart3 size={22} style={{ color: "#B8942F" }} />
            </div>
            <motion.h1 initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              style={{ fontSize: 24, fontWeight: 700, color: "var(--ah-text)", margin: 0 }}>Reports & Analytics</motion.h1>
          </div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            style={{ fontSize: 13, color: "var(--ah-muted)", marginTop: 4 }}>Build, schedule, and explore hiring intelligence</motion.p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => setBuilderOpen(!builderOpen)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 10, border: "none", backgroundColor: "var(--ah-primary)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <Plus size={14} /> New Report
          </motion.button>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 10, border: "1px solid var(--ah-border)", backgroundColor: "var(--ah-card)", color: "var(--ah-text)", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
            <Calendar size={14} /> Schedule
          </motion.button>
        </div>
      </div>

      {/* ── KPI Row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 24 }}>
        {kpis.map((k, i) => <KPICard key={k.label} kpi={k} i={i} />)}
      </div>

      {/* ── Report Builder (collapsible) ── */}
      <AnimatePresence>
        {builderOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }} style={{ overflow: "hidden", marginBottom: 24 }}>
            <div className="card-glass" style={{ borderRadius: 12, padding: 20, border: "1px solid rgba(123,90,196,0.15)" }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: "var(--ah-text)", margin: "0 0 16px" }}>Report Builder</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto 1fr", gap: 16, alignItems: "end" }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 500, color: "var(--ah-muted)", display: "block", marginBottom: 6 }}>Metric</label>
                  <div style={{ position: "relative" }}>
                    <select value={metric} onChange={(e) => setMetric(e.target.value)}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--ah-border)", backgroundColor: "var(--ah-card)", fontSize: 12, color: "var(--ah-text)", appearance: "none", cursor: "pointer" }}>
                      {metricOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <ChevronDown size={12} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ah-muted)", pointerEvents: "none" }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 500, color: "var(--ah-muted)", display: "block", marginBottom: 6 }}>Dimension</label>
                  <div style={{ position: "relative" }}>
                    <select value={dimension} onChange={(e) => setDimension(e.target.value)}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--ah-border)", backgroundColor: "var(--ah-card)", fontSize: 12, color: "var(--ah-text)", appearance: "none", cursor: "pointer" }}>
                      {dimensionOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <ChevronDown size={12} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ah-muted)", pointerEvents: "none" }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 500, color: "var(--ah-muted)", display: "block", marginBottom: 6 }}>Chart Type</label>
                  <div style={{ display: "flex", gap: 4 }}>
                    {chartTypes.map((ct) => (
                      <button key={ct.type} onClick={() => setChartType(ct.type)} style={selStyle(chartType === ct.type)} title={ct.label}>
                        <ct.icon size={14} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 500, color: "var(--ah-muted)", display: "block", marginBottom: 6 }}>Date Range</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input type="date" defaultValue="2026-01-01" style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: "1px solid var(--ah-border)", fontSize: 11, color: "var(--ah-text)", backgroundColor: "var(--ah-card)" }} />
                    <input type="date" defaultValue="2026-06-17" style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: "1px solid var(--ah-border)", fontSize: 11, color: "var(--ah-text)", backgroundColor: "var(--ah-card)" }} />
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={runPreview}
                  style={{ padding: "8px 20px", borderRadius: 8, border: "none", backgroundColor: "var(--ah-primary)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Generate</motion.button>
                <button style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--ah-border)", backgroundColor: "var(--ah-card)", color: "var(--ah-text)", fontSize: 12, cursor: "pointer" }}>Save Report</button>
                <button style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid var(--ah-border)", backgroundColor: "var(--ah-card)", color: "var(--ah-text)", fontSize: 12, cursor: "pointer" }}>Schedule</button>
              </div>
              <AnimatePresence>
                {generated && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    style={{ marginTop: 16, overflow: "hidden" }}>
                    <div style={{ padding: 16, backgroundColor: "var(--ah-surface)", borderRadius: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ah-text)", marginBottom: 12 }}>{metric} by {dimension}</div>
                      {previewBusy ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 180, gap: 8, color: "var(--ah-muted)", fontSize: 12 }}>
                          <Loader2 size={14} className="animate-spin" /> Running query…
                        </div>
                      ) : previewError ? (
                        <EmptyState icon={Filter} title="Query failed" subtitle={previewError} color="#D4572D" />
                      ) : generatedBarData.length === 0 ? (
                        <EmptyState
                          icon={BarChart3}
                          title="No data for this metric"
                          subtitle={`The engine returned no rows for ${metric}. Dimension grouping (${dimension}) is not applied server-side yet.`}
                          color="#64748B"
                        />
                      ) : (
                      <div style={{ height: 180 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={generatedBarData} margin={{ top: 0, right: 0, left: -15, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" vertical={false} />
                            <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 9 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                            <YAxis tick={{ fill: "#94A3B8", fontSize: 9 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                            <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, fontSize: 11 }} />
                            <Bar dataKey="value" fill="var(--ah-primary)" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Saved Reports Grid ── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--ah-text)", margin: "0 0 12px" }}>Saved Reports</h2>
        {reportsLoading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
            <Loader2 size={24} style={{ color: "#B8942F", animation: "spin 1s linear infinite" }} />
          </div>
        ) : savedReports.length === 0 ? (
          <div className="card-glass" style={{ borderRadius: 12 }}>
            <EmptyState icon={FileText} title="No saved reports yet" subtitle="Create your first report using the Report Builder above" color="#B8942F" />
          </div>
        ) : (
          <div className="perspective-container" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
            {savedReports.map((rep, i) => {
              const bc = moduleBadgeColors[rep.module] || "#64748B";
              return (
                <motion.div key={rep.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.03 }}
                  className="card-glass card-3d hover-shimmer" whileHover={{ y: -3, boxShadow: "0 12px 40px rgba(0,0,0,0.1)" }}
                  style={{ borderRadius: 12, padding: 16, cursor: "default" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 16 }}>{rep.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ah-text)" }}>{rep.name}</span>
                      </div>
                      <p style={{ fontSize: 11, color: "var(--ah-muted)", margin: 0 }}>{rep.description}</p>
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 99, backgroundColor: `${bc}15`, color: bc, whiteSpace: "nowrap" }}>{rep.module}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className="font-mono" style={{ fontSize: 11, color: "var(--ah-text)", fontWeight: 600 }}>{rep.records.toLocaleString()} records</span>
                      <span style={{ fontSize: 10, color: "var(--ah-muted)" }}>{rep.lastRun}</span>
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      {[{ Icon: Play, title: "Run" }, { Icon: Edit3, title: "Edit" }, { Icon: Calendar, title: "Schedule" }, { Icon: Download, title: "Export" }, { Icon: Trash2, title: "Delete" }].map(({ Icon, title }) => (
                        <motion.button key={title} whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }} title={title}
                          style={{ width: 24, height: 24, borderRadius: 6, border: "none", backgroundColor: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ah-muted)" }}>
                          <Icon size={12} />
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pre-built Analytics — 2x2 ── */}
      <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--ah-text)", margin: "0 0 12px" }}>Pre-built Analytics</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <ApplicationFunnel period={period} />
        <SourceAttribution period={period} />
        <TimeToHireTrend />
        <HiringGoals period={period} />
      </div>

      {/* ── Advanced Analytics ── */}
      <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--ah-text)", margin: "0 0 12px" }}>Advanced Analytics</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
        {/* Diversity Metrics */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
          className="card-glass" style={{ borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 11, fontWeight: 600, color: "var(--ah-muted)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 14px" }}>Diversity Metrics</h3>
          {diversityRows.length === 0 ? (
            <EmptyState
              icon={Filter}
              title="No diversity data"
              subtitle="Needs the diversity_pipeline metric to be computed; nothing has populated the analytics metric store yet."
              color="#64748B"
            />
          ) : (
          diversityRows.map((d, i) => (
            <motion.div key={d.label} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.95 + i * 0.05 }}
              style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontSize: 12, color: "var(--ah-muted)" }}>{d.label}</span>
                <span className="font-kpi" style={{ fontSize: 13, fontWeight: 700, color: d.color }}>{d.value}</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: "rgba(0,0,0,0.04)", overflow: "hidden" }}>
                <motion.div initial={{ width: 0 }} animate={{ width: `${d.bar}%` }}
                  transition={{ delay: 1 + i * 0.05, duration: 0.7 }}
                  style={{ height: "100%", borderRadius: 3, background: d.color }} />
              </div>
            </motion.div>
          )))}
        </motion.div>

        {/* Recruiter Performance */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95 }}
          className="card-glass" style={{ borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 11, fontWeight: 600, color: "var(--ah-muted)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 14px" }}>Recruiter Performance</h3>
          {recruiterRows.length === 0 ? (
            <EmptyState
              icon={Target}
              title="No recruiter activity"
              subtitle="Needs the recruiter_activity metric to be computed; nothing has populated the analytics metric store yet."
              color="#64748B"
            />
          ) : (
          recruiterRows.map((rp, i) => (
            <motion.div key={rp.name} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 + i * 0.05 }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < 3 ? "1px solid rgba(0,0,0,0.04)" : "none" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${rp.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: rp.color }}>
                {rp.name.split(" ").map((n) => n[0]).join("")}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ah-text)" }}>{rp.name}</div>
                <div style={{ fontSize: 10, color: "var(--ah-muted)" }}>{rp.screenings} screenings · {rp.avgTTH}d avg TTH</div>
              </div>
              <span className="font-kpi" style={{ fontSize: 16, fontWeight: 700, color: rp.color }}>{rp.hires}</span>
            </motion.div>
          )))}
        </motion.div>

        {/* Cost Per Hire — deliberately not wired.
            There is no cost data anywhere in the schema and no cost metric in
            MetricsEngine.ALL_METRICS, so every figure here was invented. Showing
            an explicit "not tracked" state beats five convincing fake rupee
            amounts and a "Total Avg Cost/Hire" total. */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
          className="card-glass" style={{ borderRadius: 12, padding: 18 }}>
          <h3 style={{ fontSize: 11, fontWeight: 600, color: "var(--ah-muted)", textTransform: "uppercase", letterSpacing: "0.05em", margin: "0 0 14px" }}>Cost Per Hire Breakdown</h3>
          <EmptyState
            icon={FileText}
            title="Cost per hire isn't tracked"
            subtitle="No spend or channel-cost data is captured on this platform, so this cannot be reported yet."
            color="#64748B"
          />
        </motion.div>
      </div>

      {/* ── Scheduled Reports ──
          No backend for this yet: common-service has no scheduled-report entity,
          controller or job. The previous table was hardcoded sample rows, which
          read as though scheduling was live. Say so plainly instead. */}
      <div className="card-glass" style={{ borderRadius: 12, padding: 20, marginBottom: 24 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, color: "var(--ah-text)", margin: "0 0 14px" }}>Scheduled Reports</h3>
        <EmptyState
          icon={Calendar}
          title="Report scheduling isn't available yet"
          subtitle="Recurring delivery has no backend support on this deployment. Saved reports can still be run on demand from the list above."
          color="#64748B"
        />
      </div>

      {/* ── Export Bar ── */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <div style={{ position: "relative" }}>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => setExportOpen(!exportOpen)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid var(--ah-border)", backgroundColor: "var(--ah-card)", color: "var(--ah-text)", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
            <Download size={13} /> Export All <ChevronDown size={11} />
          </motion.button>
          <AnimatePresence>
            {exportOpen && (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                style={{ position: "absolute", bottom: "110%", right: 0, backgroundColor: "var(--ah-card)", border: "1px solid var(--ah-border)", borderRadius: 8, padding: 4, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", minWidth: 120, zIndex: 10 }}>
                {["PDF", "Excel", "CSV"].map((fmt) => (
                  <div key={fmt} onClick={() => setExportOpen(false)}
                    style={{ padding: "6px 12px", fontSize: 12, color: "var(--ah-text)", cursor: "pointer", borderRadius: 6 }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--ah-surface)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
                    <FileText size={11} style={{ marginRight: 6, verticalAlign: "middle" }} />{fmt}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid var(--ah-border)", backgroundColor: "var(--ah-card)", color: "var(--ah-text)", fontSize: 12, fontWeight: 500, cursor: "pointer" }}>
          <Printer size={13} /> Print
        </motion.button>
      </div>
    </motion.div>
  );
}
